import React, { useState, useRef, useCallback } from "react";
import {
  Upload,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
} from "lucide-react";
import { bankTransactionsApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";

interface ColumnMapping {
  date: number;
  description: number;
  debit: number | null;
  credit: number | null;
  amount: number | null;
  balance: number | null;
}

interface PreviewData {
  headers: string[];
  sampleRows: string[][];
  totalRows: number;
  mapping: ColumnMapping;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  bankAccountId: string;
  bankAccountName: string;
}

type Step = "upload" | "preview" | "success";

function formatCents(cents: number): string {
  const isNegative = cents < 0;
  const abs = Math.abs(cents);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(abs / 100);
  return isNegative ? `-${formatted}` : formatted;
}

const TransactionImportModal: React.FC<Props> = ({
  open,
  onClose,
  onImported,
  bankAccountId,
  bankAccountName,
}) => {
  const [step, setStep] = useState<Step>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [importResult, setImportResult] = useState<{
    imported: number;
    skippedRows: number;
    errors: string[];
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const reset = () => {
    setStep("upload");
    setFile(null);
    setPreview(null);
    setMapping(null);
    setLoading(false);
    setError("");
    setImportResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = useCallback(async (selectedFile: File) => {
    setFile(selectedFile);
    setError("");
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const res = await bankTransactionsApi.preview(formData);
      const data = res.data as PreviewData;

      setPreview(data);
      setMapping(data.mapping);
      setStep("preview");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to parse CSV file");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) handleFile(droppedFile);
    },
    [handleFile]
  );

  const handleImport = async () => {
    if (!file || !mapping) return;
    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("bankAccountId", bankAccountId);
      formData.append("mapping", JSON.stringify(mapping));

      const res = await bankTransactionsApi.import(formData);
      setImportResult(res.data);
      setStep("success");
      onImported();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to import transactions");
    } finally {
      setLoading(false);
    }
  };

  const columnOptions = preview
    ? [
        { value: "-1", label: "(Skip)" },
        ...preview.headers.map((h, i) => ({
          value: String(i),
          label: `${i + 1}. ${h}`,
        })),
      ]
    : [];

  return (
    <PremiumModal
      open={open}
      onClose={handleClose}
      title="Import Bank Transactions"
      icon={FileSpreadsheet}
      size="lg"
      footer={
        <div className="flex justify-between items-center w-full">
          <div className="text-sm text-slate-500">
            {step === "preview" && preview && (
              <span>{preview.totalRows} rows detected</span>
            )}
          </div>
          <div className="flex gap-3">
            {step === "preview" && (
              <button
                onClick={() => { reset(); }}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Back
              </button>
            )}
            {step === "preview" && (
              <button
                onClick={handleImport}
                disabled={loading || !mapping || mapping.date === -1}
                className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Importing...
                  </span>
                ) : (
                  `Import ${preview?.totalRows || 0} Transactions`
                )}
              </button>
            )}
            {step === "success" && (
              <button
                onClick={handleClose}
                className="px-5 py-2.5 rounded-xl text-white text-sm font-bold"
                style={{ backgroundColor: "var(--color-primary)" }}
              >
                Done
              </button>
            )}
          </div>
        </div>
      }
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Step 1: Upload */}
      {step === "upload" && (
        <div>
          <p className="text-sm text-slate-600 mb-4">
            Upload a CSV file from your bank for <span className="font-semibold">{bankAccountName}</span>.
            Supports all major Canadian banks (TD, RBC, BMO, Scotia, CIBC).
          </p>

          <div
            className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
              dragging
                ? "border-brand-accent bg-brand-light"
                : "border-slate-300 hover:border-slate-400 bg-slate-50"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {loading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-3" />
                <p className="text-sm font-medium text-slate-600">Parsing CSV...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="h-10 w-10 text-slate-400 mb-3" />
                <p className="text-sm font-medium text-slate-700">
                  Drop your CSV file here or click to browse
                </p>
                <p className="text-xs text-slate-400 mt-1">CSV files up to 10 MB</p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
        </div>
      )}

      {/* Step 2: Preview + Column Mapping */}
      {step === "preview" && preview && mapping && (
        <div>
          <p className="text-sm text-slate-600 mb-4">
            Review the detected column mapping and adjust if needed.
          </p>

          {/* Column Mapping */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            <div>
              <label className="block text-xs text-slate-500 mb-1 font-medium">Date Column *</label>
              <SearchableSelect
                options={columnOptions}
                value={String(mapping.date)}
                onChange={(val) => setMapping({ ...mapping, date: parseInt(val || "0") })}
                placeholder="Select column"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1 font-medium">Description Column *</label>
              <SearchableSelect
                options={columnOptions}
                value={String(mapping.description)}
                onChange={(val) => setMapping({ ...mapping, description: parseInt(val || "1") })}
                placeholder="Select column"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1 font-medium">Amount (combined)</label>
              <SearchableSelect
                options={columnOptions}
                value={mapping.amount !== null ? String(mapping.amount) : "-1"}
                onChange={(val) => {
                  const idx = parseInt(val || "-1");
                  setMapping({
                    ...mapping,
                    amount: idx >= 0 ? idx : null,
                    debit: idx >= 0 ? null : mapping.debit,
                    credit: idx >= 0 ? null : mapping.credit,
                  });
                }}
                placeholder="Select column"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1 font-medium">Debit (withdrawal)</label>
              <SearchableSelect
                options={columnOptions}
                value={mapping.debit !== null ? String(mapping.debit) : "-1"}
                onChange={(val) => {
                  const idx = parseInt(val || "-1");
                  setMapping({
                    ...mapping,
                    debit: idx >= 0 ? idx : null,
                    amount: idx >= 0 ? null : mapping.amount,
                  });
                }}
                placeholder="Select column"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1 font-medium">Credit (deposit)</label>
              <SearchableSelect
                options={columnOptions}
                value={mapping.credit !== null ? String(mapping.credit) : "-1"}
                onChange={(val) => {
                  const idx = parseInt(val || "-1");
                  setMapping({
                    ...mapping,
                    credit: idx >= 0 ? idx : null,
                    amount: idx >= 0 ? null : mapping.amount,
                  });
                }}
                placeholder="Select column"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1 font-medium">Balance</label>
              <SearchableSelect
                options={columnOptions}
                value={mapping.balance !== null ? String(mapping.balance) : "-1"}
                onChange={(val) => {
                  const idx = parseInt(val || "-1");
                  setMapping({ ...mapping, balance: idx >= 0 ? idx : null });
                }}
                placeholder="Select column"
              />
            </div>
          </div>

          {/* Preview Table */}
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-white" style={{ backgroundColor: "var(--color-primary)" }}>
                  <th className="px-3 py-2 text-left font-semibold">#</th>
                  {preview.headers.map((h, i) => (
                    <th key={i} className="px-3 py-2 text-left font-semibold whitespace-nowrap">
                      {h}
                      {i === mapping.date && <span className="ml-1 text-emerald-200">(Date)</span>}
                      {i === mapping.description && <span className="ml-1 text-emerald-200">(Desc)</span>}
                      {i === mapping.amount && <span className="ml-1 text-emerald-200">(Amt)</span>}
                      {i === mapping.debit && <span className="ml-1 text-emerald-200">(Dr)</span>}
                      {i === mapping.credit && <span className="ml-1 text-emerald-200">(Cr)</span>}
                      {i === mapping.balance && <span className="ml-1 text-emerald-200">(Bal)</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {preview.sampleRows.map((row, ri) => (
                  <tr key={ri} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-400">{ri + 1}</td>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className={`px-3 py-2 whitespace-nowrap ${
                          ci === mapping.date ||
                          ci === mapping.description ||
                          ci === mapping.amount ||
                          ci === mapping.debit ||
                          ci === mapping.credit
                            ? "text-slate-800 font-medium"
                            : "text-slate-500"
                        }`}
                      >
                        {cell || "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.totalRows > 10 && (
            <p className="text-xs text-slate-400 mt-2">
              Showing first 10 of {preview.totalRows} rows
            </p>
          )}
        </div>
      )}

      {/* Step 3: Success */}
      {step === "success" && importResult && (
        <div className="text-center py-6">
          <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-emerald-500" />
          <h3 className="text-xl font-bold text-slate-800 mb-2">Import Complete</h3>
          <p className="text-sm text-slate-600 mb-4">
            Successfully imported <span className="font-bold text-emerald-700">{importResult.imported}</span> transactions
            into <span className="font-semibold">{bankAccountName}</span>.
          </p>
          {importResult.skippedRows > 0 && (
            <p className="text-sm text-amber-600 mb-2">
              {importResult.skippedRows} rows were skipped (invalid data)
            </p>
          )}
          {importResult.errors.length > 0 && (
            <div className="mt-3 text-left bg-amber-50 rounded-lg p-3">
              <p className="text-xs font-medium text-amber-700 mb-1">Parse warnings:</p>
              {importResult.errors.slice(0, 5).map((err, i) => (
                <p key={i} className="text-xs text-amber-600">{err}</p>
              ))}
              {importResult.errors.length > 5 && (
                <p className="text-xs text-amber-500 mt-1">
                  ...and {importResult.errors.length - 5} more
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </PremiumModal>
  );
};

export default TransactionImportModal;
